import { TypeActorEnum } from "../../../../../helpers/GlobalEnum";
import { reportHeaderImg1, reportHeaderImg2 } from "./reportAssets";

/**
 * Documento imprimible: "MANIFESTACIÓN LIBRE, EXPRESA E INFORMADA ... EN EL MARCO DEL PNIS"
 * (Consentimiento de Renegociación - formato PP-SCI-01.V1).
 *
 * Reproduce el formato oficial de 4 páginas. La sección "Manifestación del titular
 * del CUB" (página 1) se auto-diligencia con los datos que devuelve
 * reportServices.headlineReport(cubId); el resto de campos quedan en blanco para
 * ser diligenciados a mano, igual que en el documento físico.
 *
 * Se renderiza oculto y se imprime vía print-js (mismo patrón que
 * ConsolidatedPurchaseReport / BalanceInFavorReport).
 */
export const RenegotiationConsentReport = ({ dataReport }) => {
    // Marca [X] la calidad registrada según la actividad del titular.
    const mark = (value) => (dataReport?.actividad === value ? "X" : " ");

    const line = { borderBottom: "1px solid #000", display: "inline-block", minWidth: "60px" };
    const fill = (value, minWidth = "200px") => (
        <span style={{ ...line, minWidth }}>&nbsp;{value ?? ""}&nbsp;</span>
    );

    const sectionTitle = { fontWeight: "bold", marginTop: "12px", marginBottom: "6px" };
    const paragraph = { textAlign: "justify", margin: "6px 0", lineHeight: 1.4 };
    const field = { margin: "8px 0" };

    // Pie de página repetido en cada hoja impresa (vía <tfoot>, que el navegador
    // reimprime al final de cada página).
    const pageFooter = (
        <div style={{ textAlign: "center", marginTop: "40px", paddingTop: "12px", borderTop: "1px solid #000", fontSize: "12px" }}>
            Dirección de Sustitución de Cultivos de Uso Ilícito — Carrera 7 No. 26-20, Bogotá D. C.,
            Colombia. Edificio Tequendama (Pisos 29, 32 y 33) — Teléfono: 4221030, Ext 7002
            <br />
            PP-SCI-01.V1 — Publicado: 28-06-2024
        </div>
    );

    return (
        <table id="renegotiation-consent-section" style={{ fontSize: "13px", width: "100%", borderCollapse: "collapse" }}>
            <tfoot>
                <tr>
                    <td>{pageFooter}</td>
                </tr>
            </tfoot>
            <tbody>
            <tr>
            <td>
            {/* Encabezado institucional */}
            <div style={{ textAlign: "center", marginBottom: "10px" }}>
                <img src={reportHeaderImg1} alt="Agencia de Renovación del Territorio" style={{ height: "60px", margin: "0 15px" }} />
                <img src={reportHeaderImg2} alt="Dirección de Sustitución de Cultivos de Uso Ilícito" style={{ height: "60px", margin: "0 15px" }} />
            </div>
            <div style={{ textAlign: "center", marginBottom: "10px" }}>
                <strong>AGENCIA DE RENOVACIÓN DEL TERRITORIO</strong>
                <br />
                <strong>DIRECCIÓN DE SUSTITUCIÓN DE CULTIVOS DE USO ILÍCITO</strong>
            </div>

            <div style={{ textAlign: "center", fontWeight: "bold", margin: "14px 0" }}>
                MANIFESTACIÓN LIBRE, EXPRESA E INFORMADA PARA LA ACTUALIZACIÓN DE DATOS, LA
                NOTIFICACIÓN ELECTRÓNICA, LA CONTINUIDAD DE LA RENEGOCIACIÓN, EL TRATAMIENTO DE
                SALDOS EN EL MARCO DEL PNIS
            </div>

            {/* Objeto y alcance */}
            <div style={sectionTitle}>Objeto y alcance</div>
            <p style={paragraph}>
                El presente instrumento permite al titular del núcleo familiar beneficiario del
                Programa Nacional Integral de Sustitución de Cultivos de Uso Ilícito (PNIS) expresar,
                de manera libre, separada e informada, las decisiones que resulten aplicables a su
                caso respecto de: (i) la actualización y el tratamiento de datos personales; (ii) la
                notificación electrónica; (iii) la renegociación regulada por la Resolución 0021 de
                2024; (iv) el tratamiento y cierre de saldos remanentes previsto en la Resolución 0044
                de 2026; y (v) la solicitud de tratamiento diferencial regulada por la Resolución 0027
                de 2024, modificada por la Resolución 0043 de 2026.
            </p>
            <p style={paragraph}>
                Cada manifestación opera únicamente cuando el titular marque la opción correspondiente
                y suministre la información exigida. La firma de este formato no constituye una
                renuncia general a derechos, no sustituye el Plan de Inversión ni los actos
                administrativos que deba expedir la DSCI, y no implica aprobación automática,
                transferencia inmediata de recursos ni cierre del proceso sin las validaciones y
                actuaciones legalmente requeridas.
            </p>

            {/* Manifestación del titular del CUB (auto-diligenciado) */}
            <div style={sectionTitle}>Manifestación del titular del CUB</div>

            <div style={field}>Nombre completo: {fill(dataReport?.nombre, "400px")}</div>

            <div style={field}>
                Tipo y número de documento: [{fill(dataReport?.tipo_documento, "80px")}]{" "}
                {fill(dataReport?.cedula, "300px")}
            </div>

            <div style={field}>Código Único de Beneficiario (CUB): {fill(dataReport?.cub_id, "120px")}</div>

            <div style={field}>
                Calidad registrada: [ {mark(TypeActorEnum.CULTIVATOR)} ] Cultivador(a)
                &nbsp;&nbsp;&nbsp;[ {mark(TypeActorEnum.NO_GROWER)} ] No cultivador(a)
                &nbsp;&nbsp;&nbsp;[ {mark(TypeActorEnum.COLLECTOR)} ] Recolector(a)
            </div>

            <div style={field}>
                Municipio: {fill(dataReport?.municipio, "250px")}
                &nbsp;&nbsp;Vereda: {fill(dataReport?.vereda, "250px")}
            </div>

            <div style={field}>Departamento: {fill(dataReport?.departamento, "300px")}</div>

            {/* 1. Constancia */}
            <div style={sectionTitle}>1. Constancia de información, comprensión y voluntariedad</div>
            <p style={paragraph}>
                Declaro que, antes de suscribir este documento, recibí una explicación clara y
                suficiente sobre su contenido, las alternativas disponibles, los requisitos, los
                efectos jurídicos y los canales para formular preguntas o reclamaciones. Tuve
                oportunidad real de preguntar, solicitar aclaraciones y decidir sin presión,
                condicionamiento indebido ni diligenciamiento de espacios en blanco.
            </p>
            <p style={paragraph}>
                La información fue: [ ] leída directamente por mí&nbsp;&nbsp;[ ] leída en voz alta por
                personal de la DSCI&nbsp;&nbsp;[ ] explicada con apoyo de intérprete, facilitador o
                ajuste razonable. Idioma, apoyo o ajuste utilizado (si aplica): {fill("", "300px")}.
            </p>

            {/* 2. Actualización de datos */}
            <div style={sectionTitle}>2. Actualización y tratamiento de datos personales</div>
            <div style={field}>Dirección o lugar de residencia: {fill("", "400px")}</div>
            <div style={field}>
                Teléfono celular: {fill("", "200px")}&nbsp;&nbsp;Teléfono alterno: {fill("", "200px")}
            </div>
            <div style={field}>Correo electrónico: {fill("", "400px")}</div>

            {/* 3. Notificación electrónica */}
            <div style={sectionTitle}>3. Autorización de notificación electrónica</div>
            <p style={paragraph}>
                Al proveer mis datos de canales electrónicos como correo o teléfono de celular en este
                formato, <strong>autorizo que los actos administrativos que deban notificárseme se
                pongan a disposición mediante el servicio de notificaciones de la sede electrónica de
                la ART o el mecanismo institucional habilitado conforme al artículo 56 de la Ley 1437
                de 2011</strong>. Entiendo que la notificación queda surtida en la fecha y hora en que
                quede disponible para mi acceso, lo cual deberá ser certificado por la Administración.
                Durante la actuación podré solicitar que las notificaciones sucesivas se practiquen por
                otro medio legal, salvo que el uso de medios electrónicos sea obligatorio.
            </p>

            {/* 4. Renegociación */}
            <div style={sectionTitle}>4. Renegociación en los términos de la Resolución 0021 de 2024</div>
            <p style={paragraph}>Marque lo que corresponda (solo una opción):</p>
            <p style={paragraph}>
                [ ] Ratifico que suscribí voluntariamente mi Plan de Inversión el
                ___/___/______ y manifiesto mi voluntad de continuar con su implementación.
            </p>
            <p style={paragraph}>
                [ ] Solicito continuar la formulación o suscripción del Plan de Inversión, porque el
                trámite aún no ha culminado. Línea o actividad productiva: {fill(dataReport?.linea, "300px")}
            </p>
            <p style={paragraph}>
                Entiendo que la renegociación procede por una sola vez y recae sobre los recursos
                disponibles del Plan de Atención Inmediata Familiar, descontados los componentes ya
                entregados y las sumas previamente transferidas. La DSCI debe verificar los
                presupuestos técnicos y jurídicos del Plan de Inversión y expedir el acto
                administrativo de aprobación o improbación; por tanto, este formato no reemplaza el
                Plan de Inversión ni dicho acto, ni reabre una renegociación ya agotada.
            </p>
            <p style={paragraph}>
                Me comprometo a participar en las jornadas, ferias, verificaciones o mecanismos de
                implementación que sean previamente informados por la DSCI y resulten necesarios para
                ejecutar mi Plan de Inversión. La programación dependerá de las condiciones
                presupuestales, operativas, administrativas y logísticas, sin perjuicio del deber de
                la Administración de actuar con diligencia, informar oportunamente las novedades y
                adoptar medidas para la continuidad material del PNIS.
            </p>

            {/* 5. Tratamiento y cierre de saldos remanentes */}
            <div style={sectionTitle}>5. Tratamiento y cierre de saldos remanentes - Resolución 0044 de 2026</div>
            <p style={paragraph}>Información sujeta a validación técnica, administrativa y financiera por la DSCI:</p>
            <div style={field}>Plan de Inversión o acto de aprobación {fill("", "400px")}.</div>

            <div style={{ ...sectionTitle, marginLeft: "16px" }}>
                5.1. Saldo igual o inferior a diez mil pesos ($10.000 M/L)
            </div>
            <p style={paragraph}>
                [ ] Declaro que fui informado(a) de que, si la DSCI valida que mi saldo se encuentra
                entre un peso ($1) y diez mil pesos ($10.000), el cierre operativo, administrativo y
                financiero se realizará conforme los efectos que se señalan en la Resolución 0044 de
                2026, por razón de su mínima cuantía, y los recursos serán incorporados o reorientados
                globalmente al componente de Asistencia Técnica Integral. Esta constancia no
                constituye una autorización individual ni una renuncia a derechos o reclamaciones, pues
                el tratamiento deriva directamente de los efectos de la resolución antedicha.
            </p>

            <div style={{ ...sectionTitle, marginLeft: "16px" }}>
                5.2. Saldo superior a diez mil pesos ($10.000) y hasta un millón de pesos ($1.000.000 M/L)
            </div>
            <p style={paragraph}>
                [ ] Autorizo, por una única vez y únicamente respecto del saldo que certifique la DSCI
                dentro de este rango, su transferencia monetaria <strong>a mi favor en una cuenta a mi
                nombre</strong>, mediante el mecanismo financiero que resulte procedente.
            </p>
            <div style={field}>
                Entidad financiera: {fill("", "250px")}&nbsp;&nbsp;Tipo de cuenta: [ ] Ahorros&nbsp;&nbsp;[ ] Corriente&nbsp;&nbsp;[ ] Trámite simplificado / Número de cuenta: {fill("", "250px")}.
            </div>

            <div style={{ ...sectionTitle, marginLeft: "16px" }}>
                5.3. Renuncia voluntaria y específica a saldos
                <span style={{ fontWeight: "normal", fontStyle: "italic" }}> (Opción independiente, incompatible con el diligenciamiento de los numerales 5.1 y 5.2)</span>
            </div>
            <p style={paragraph}>
                [ ] RENUNCIO voluntaria, expresa e informadamente al saldo de $ {fill("", "250px")},
                previamente certificado por la DSCI, y solicito que se registre y tramite conforme al
                procedimiento institucional. Si se marca esta opción, con monto cierto y firma del
                titular, podrá interpretarse como renuncia a los saldos para la finalización en la
                atención del programa; los espacios en blanco o la ausencia de marca significan que no
                existe renuncia.
            </p>

            {/* 6. Declaraciones finales */}
            <div style={sectionTitle}>6. Declaraciones finales y salvaguardas</div>
            <p style={paragraph}>
                Declaro que la información suministrada es veraz, completa y actualizada, y autorizo su
                verificación para las finalidades indicadas. La existencia de inconsistencias dará
                lugar a las actuaciones administrativas y legales correspondientes, con garantía de
                comunicación, contradicción, defensa, motivación y recursos (cuando aplique); la
                suscripción de este formato no autoriza exclusiones o sanciones automáticas.
            </p>
            <p style={paragraph}>
                Declaro que recibí o se me ofreció una copia íntegra de este documento y que conozco
                los canales institucionales para solicitar aclaraciones, actualizar información,
                presentar recursos, peticiones, quejas o reclamos (EnlaceCiudadano@renovacionterritorio.gov.co).
                La firma de quien acompaña por la DSCI deja constancia de la orientación y recepción,
                pero <strong>no constituye aprobación de ninguna de las solicitudes contenidas en este
                ni certificación de la veracidad de los hechos declarados</strong>.
            </p>

            {/* 7. Documentos anexos */}
            <div style={sectionTitle}>7. Documentos anexos y constancias</div>
            <div style={field}>
                Se anexan, según corresponda: [ ] copia del documento de identidad&nbsp;&nbsp;[ ]
                certificación bancaria&nbsp;&nbsp;[ ] soporte del Plan de Inversión, [ ] otro/s: {fill("", "300px")}.
            </div>
            <div style={field}>Lugar de suscripción: {fill("", "400px")}.</div>
            <div style={field}>
                Fecha: ___/___/______&nbsp;&nbsp;&nbsp;&nbsp;Hora: {fill("", "120px")}
            </div>
            <div style={field}>
                Nombre de intérprete, facilitador, testigo o persona de apoyo (si aplica): {fill("", "300px")}
            </div>
            <div style={field}>
                Documento/rol: {fill("", "250px")}&nbsp;&nbsp;Firma: {fill("", "250px")}
            </div>
            <p style={paragraph}>
                Después de leer o recibir explicación integral del documento, manifiesto que las
                opciones marcadas reflejan mi decisión libre, expresa e informada. Iniciales del
                titular en cada página.
            </p>

            {/* Firmas */}
            <table style={{ borderCollapse: "collapse", width: "100%", marginTop: "12px" }}>
                <tbody>
                    <tr>
                        <td style={{ border: "1px solid #000", height: "140px", verticalAlign: "top", padding: "6px", width: "50%" }}>
                            FIRMA TITULAR QUE REALIZÓ LA SOLICITUD.
                            <div style={{ marginTop: "60px" }}>C.C: {dataReport?.cedula}</div>
                            <div style={{ marginTop: "10px" }}>Huella</div>
                        </td>
                        <td style={{ border: "1px solid #000", height: "140px", verticalAlign: "top", padding: "6px", width: "50%" }}>
                            FIRMA DE QUIEN ACOMPAÑÓ – DSCI
                            <div style={{ marginTop: "60px" }}>Rol: ______________________________</div>
                        </td>
                    </tr>
                </tbody>
            </table>
            </td>
            </tr>
            </tbody>
        </table>
    );
};